/**
 * `<ConnectionStateStrip>` — section 1 of the B-1 VM Debug pane.
 *
 * Sticky top strip showing the current `VmConnectionState` + recent
 * lifecycle event. Subscribes to the SAME `vm:connection-state` push
 * A-1's pill consumes (no separate subscription path) so the two
 * surfaces never drift.
 *
 * Renders nothing when no `vm:connection-state` push has been
 * received yet. The pane stays mounted but the strip is silent until
 * main side dispatches its first transition.
 */

import { useEffect, useState } from 'react';
import type {
  VmConnectionState,
  VmConnectionStatePush,
} from '@volley/crucible-ipc-types/vm-connection-state';
import type { LifecycleEvent } from '@volley/crucible-ipc-types/lifecycle-event';

const STATE_LABEL: Record<VmConnectionState, string> = {
  idle: 'Idle',
  connecting: 'Connecting',
  connected: 'Connected',
  reconnecting: 'Reconnecting',
  unreachable: 'Unreachable',
};

const STATE_GLYPH: Record<VmConnectionState, string> = {
  idle: 'O',
  connecting: '…',
  connected: '●',
  reconnecting: '~',
  unreachable: '!',
};

export function ConnectionStateStrip(): React.JSX.Element | null {
  const [state, setState] = useState<VmConnectionStatePush | null>(null);
  const [lastLifecycle, setLastLifecycle] = useState<LifecycleEvent | null>(
    null,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.crucible) return;
    const unsubState = window.crucible.on(
      'vm:connection-state',
      (push: VmConnectionStatePush) => {
        setState(push);
      },
    );
    const unsubLifecycle = window.crucible.on(
      'vm:lifecycle',
      (event: LifecycleEvent) => {
        setLastLifecycle(event);
      },
    );
    return () => {
      unsubState();
      unsubLifecycle();
    };
  }, []);

  if (state === null) return null;

  return (
    <div
      data-testid="vm-debug-connection-strip"
      className="sticky top-0 z-10 flex items-center gap-3 border-b border-white/10 bg-black/40 px-3 py-2 text-xs"
    >
      <span
        data-testid="vm-debug-connection-state"
        data-vm-state={state.state}
        className="inline-flex items-center gap-1 font-medium"
      >
        <span aria-hidden="true">{STATE_GLYPH[state.state]}</span>
        {STATE_LABEL[state.state]}
      </span>
      {state.lastSuccessfulRoundTripAgoMs !== null ? (
        <span className="text-white/50">
          RTT {state.lastSuccessfulRoundTripAgoMs}ms
        </span>
      ) : null}
      {lastLifecycle ? (
        <span className="text-white/40">
          last: {lastLifecycle.code} ({lastLifecycle.category})
        </span>
      ) : null}
    </div>
  );
}
